import React, { useEffect, useState } from "react";
import axios from "axios";
import { useI18n } from "../lib/i18n";
import {
  CheckCircle,
  ArrowRight,
  Sparkle,
  Crown,
  ChatCircle,
  CircleNotch,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map package_id -> i18n key in t.shop.packages (membership uses 'investitore' key + tier)
const ENTRY_PKGS = ["sos-casa", "airbnb-check", "pre-asta"];
const PREMIUM_PKGS = ["relooking", "airbnb-ready"];
const MEMBERSHIP_TIERS = [
  { id: "investitore-base", tier: "base" },
  { id: "investitore-plus", tier: "plus" },
  { id: "investitore-premium", tier: "premium" },
];

function PriceTag({ amount, regular, monthly, t }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="font-serif text-5xl text-[#0A1428] font-semibold">
        € {amount}
      </span>
      {regular && (
        <span className="text-base text-[#4A5568] line-through">
          € {regular}
        </span>
      )}
      {monthly && (
        <span className="text-sm text-[#4A5568]">{t.shop.monthly}</span>
      )}
    </div>
  );
}

async function startCheckout(packageId, setLoadingId) {
  setLoadingId(packageId);
  try {
    const { data } = await axios.post(`${API}/checkout/session`, {
      package_id: packageId,
      origin_url: window.location.origin,
    });
    window.location.href = data.url;
  } catch (e) {
    console.error(e);
    alert("Errore durante il pagamento. Riprova.");
    setLoadingId(null);
  }
}

function EntryCard({ pkgId, info, copy, t, loadingId, onBuy, badge }) {
  return (
    <div
      data-testid={`pkg-card-${pkgId}`}
      className="card-elegant bg-white p-8 lg:p-9 flex flex-col"
    >
      {badge && (
        <span className="inline-block self-start text-[10px] uppercase tracking-widest font-bold text-[#0A1428] bg-[#C5A059] px-3 py-1 mb-5">
          {badge}
        </span>
      )}
      <h3 className="font-serif text-2xl lg:text-3xl text-[#0A1428] font-medium leading-tight">
        {copy.title}
      </h3>
      <p className="mt-3 italic text-[#C5A059] text-sm">{copy.tagline}</p>
      <p className="mt-4 text-[#4A5568] text-sm leading-relaxed flex-1">
        {copy.desc}
      </p>

      <ul className="mt-6 space-y-2">
        {copy.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#0A1428]">
            <CheckCircle
              size={16}
              weight="duotone"
              className="text-[#C5A059] flex-shrink-0 mt-0.5"
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-7 pt-6 border-t border-[#0A1428]/10">
        <p className="text-[10px] uppercase tracking-widest text-[#4A5568] mb-2">
          {t.shop.duration}: {copy.duration}
        </p>
        <PriceTag
          amount={info.price.toLocaleString("it-IT")}
          regular={info.regular_price ? info.regular_price.toLocaleString("it-IT") : null}
          t={t}
        />
        <button
          onClick={() => onBuy(pkgId)}
          disabled={loadingId === pkgId}
          data-testid={`pkg-buy-${pkgId}`}
          className="btn-primary w-full mt-5 justify-center disabled:opacity-50"
        >
          {loadingId === pkgId ? (
            <>
              <CircleNotch size={16} className="animate-spin" />
              {t.shop.processing}
            </>
          ) : (
            <>
              {t.shop.buyNow} <ArrowRight size={14} weight="bold" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function PremiumCard({ pkgId, info, copy, t, loadingId, onBuy }) {
  return (
    <div
      data-testid={`pkg-card-${pkgId}`}
      className="card-elegant bg-[#0A1428] text-white p-8 lg:p-10 flex flex-col"
    >
      <Crown size={28} weight="duotone" className="text-[#C5A059] mb-5" />
      <h3 className="font-serif text-2xl lg:text-3xl text-white font-medium leading-tight">
        {copy.title}
      </h3>
      <p className="mt-3 italic text-[#C5A059] text-sm">{copy.tagline}</p>
      <p className="mt-4 text-white/75 text-sm leading-relaxed flex-1">
        {copy.desc}
      </p>

      <ul className="mt-6 space-y-2">
        {copy.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/85">
            <CheckCircle
              size={16}
              weight="duotone"
              className="text-[#C5A059] flex-shrink-0 mt-0.5"
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-7 pt-6 border-t border-white/10">
        <p className="text-[10px] uppercase tracking-widest text-white/55 mb-2">
          {t.shop.duration}: {copy.duration}
        </p>
        <div className="flex items-baseline gap-2">
          <span className="text-xs uppercase tracking-widest text-[#C5A059]">
            {t.shop.starting}
          </span>
        </div>
        <div className="font-serif text-5xl text-[#C5A059] font-semibold mt-1">
          € {info.price.toLocaleString("it-IT")}
        </div>
        <button
          onClick={() => onBuy(pkgId)}
          disabled={loadingId === pkgId}
          data-testid={`pkg-buy-${pkgId}`}
          className="bg-[#C5A059] text-[#0A1428] hover:bg-[#b08d4f] px-8 py-4 font-semibold uppercase tracking-wider text-sm w-full mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
        >
          {loadingId === pkgId ? (
            <>
              <CircleNotch size={16} className="animate-spin" />
              {t.shop.processing}
            </>
          ) : (
            <>
              {t.shop.buyNow} <ArrowRight size={14} weight="bold" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function MembershipCard({ copy, t, loadingId, onBuy, packages }) {
  const [tier, setTier] = useState("base");
  const tierData = MEMBERSHIP_TIERS.find((x) => x.tier === tier);
  const info = packages.find((p) => p.id === tierData.id);

  return (
    <div
      data-testid="pkg-card-investitore"
      className="card-elegant bg-white p-8 lg:p-12 lg:col-span-3"
    >
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#C5A059] mb-4">
            <ChatCircle size={14} weight="duotone" /> Membership
          </span>
          <h3 className="font-serif text-3xl lg:text-4xl text-[#0A1428] font-medium leading-tight">
            {copy.title}
          </h3>
          <p className="mt-3 italic text-[#C5A059] text-base">{copy.tagline}</p>
          <p className="mt-4 text-[#4A5568] leading-relaxed">{copy.desc}</p>

          <ul className="mt-6 space-y-2.5">
            {copy.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-[#0A1428]">
                <CheckCircle
                  size={18}
                  weight="duotone"
                  className="text-[#C5A059] flex-shrink-0 mt-0.5"
                />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5 bg-[#F8F9FA] p-6 lg:p-8 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#4A5568] mb-3">
              Scegli il piano
            </p>
            <div className="space-y-2">
              {Object.entries(copy.tiers).map(([key, t2]) => (
                <button
                  key={key}
                  onClick={() => setTier(key)}
                  data-testid={`pkg-tier-${key}`}
                  className={`w-full flex items-center justify-between px-4 py-3 border transition-colors ${
                    tier === key
                      ? "border-[#C5A059] bg-white"
                      : "border-[#0A1428]/10 bg-transparent hover:border-[#0A1428]/30"
                  }`}
                >
                  <span className="font-semibold text-[#0A1428] text-sm">
                    {t2.label}
                  </span>
                  <span className="font-serif text-lg text-[#0A1428]">
                    € {t2.price}
                    <span className="text-xs text-[#4A5568] ml-0.5">{t.shop.monthly}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => onBuy(tierData.id)}
            disabled={loadingId === tierData.id}
            data-testid="pkg-buy-investitore"
            className="btn-primary w-full mt-6 justify-center disabled:opacity-50"
          >
            {loadingId === tierData.id ? (
              <>
                <CircleNotch size={16} className="animate-spin" />
                {t.shop.processing}
              </>
            ) : (
              <>
                {t.shop.buyNow} € {info?.price} <ArrowRight size={14} weight="bold" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ServicesOnlinePage() {
  const { t } = useI18n();
  const [packages, setPackages] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/packages`)
      .then((r) => setPackages(r.data))
      .catch((e) => console.error(e));
  }, []);

  const buy = (id) => startCheckout(id, setLoadingId);
  const getInfo = (id) => packages.find((p) => p.id === id);

  return (
    <div className="pt-32 pb-24 lg:pb-32 bg-[#F8F9FA] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl">
          <p className="overline mb-5">{t.shop.overline}</p>
          <h1
            data-testid="shop-page-title"
            className="font-serif text-5xl lg:text-6xl xl:text-7xl text-[#0A1428] leading-[1.05] tracking-tight font-medium"
          >
            {t.shop.title}
          </h1>
          <p className="mt-6 text-[#4A5568] text-lg leading-relaxed">
            {t.shop.subtitle}
          </p>
        </div>

        {/* Entry packages */}
        <div className="mt-20">
          <div className="flex items-end gap-3 mb-3">
            <Sparkle size={22} weight="duotone" className="text-[#C5A059] mb-1" />
            <h2 className="font-serif text-3xl lg:text-4xl text-[#0A1428] font-medium">
              {t.shop.categoryEntry}
            </h2>
          </div>
          <p className="text-[#4A5568] max-w-2xl mb-10">{t.shop.categoryEntryDesc}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ENTRY_PKGS.map((pkgId) => {
              const info = getInfo(pkgId);
              if (!info) return null;
              const copy = t.shop.packages[pkgId];
              const badge = pkgId === "sos-casa" ? t.shop.promoBadge : null;
              return (
                <EntryCard
                  key={pkgId}
                  pkgId={pkgId}
                  info={info}
                  copy={copy}
                  t={t}
                  loadingId={loadingId}
                  onBuy={buy}
                  badge={badge}
                />
              );
            })}
          </div>
        </div>

        {/* Premium packages */}
        <div className="mt-24">
          <div className="flex items-end gap-3 mb-3">
            <Crown size={22} weight="duotone" className="text-[#C5A059] mb-1" />
            <h2 className="font-serif text-3xl lg:text-4xl text-[#0A1428] font-medium">
              {t.shop.categoryPremium}
            </h2>
          </div>
          <p className="text-[#4A5568] max-w-2xl mb-10">
            {t.shop.categoryPremiumDesc}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {PREMIUM_PKGS.map((pkgId) => {
              const info = getInfo(pkgId);
              if (!info) return null;
              const copy = t.shop.packages[pkgId];
              return (
                <PremiumCard
                  key={pkgId}
                  pkgId={pkgId}
                  info={info}
                  copy={copy}
                  t={t}
                  loadingId={loadingId}
                  onBuy={buy}
                />
              );
            })}
          </div>
        </div>

        {/* Membership */}
        <div className="mt-24">
          <div className="flex items-end gap-3 mb-3">
            <ChatCircle size={22} weight="duotone" className="text-[#C5A059] mb-1" />
            <h2 className="font-serif text-3xl lg:text-4xl text-[#0A1428] font-medium">
              {t.shop.categoryMembership}
            </h2>
          </div>
          <p className="text-[#4A5568] max-w-2xl mb-10">
            {t.shop.categoryMembershipDesc}
          </p>

          <div>
            <MembershipCard
              copy={t.shop.packages.investitore}
              t={t}
              loadingId={loadingId}
              onBuy={buy}
              packages={packages}
            />
          </div>
        </div>

        {/* Trust footer */}
        <p className="text-center text-xs text-[#4A5568] mt-16 max-w-2xl mx-auto">
          🔒 Pagamenti sicuri tramite Stripe. Riceverai conferma all'email indicata
          al checkout. Per qualsiasi domanda: makelaars360@gmail.com — +39 393 590 2294
        </p>
      </div>
    </div>
  );
}
